// src/app/features/markets/useLivePrices.js
import { useEffect, useMemo, useRef, useState } from "react";
import { createPriceStream, normalizeSymbol, getMarketPrice } from "./marketApi.js";

export function useLivePrices(rawSymbols = [], opts = {}) {
  const enabled = opts.enabled !== false;
  const maxSymbols = Number(opts.maxSymbols || 50);

  const symbols = useMemo(() => {
    const uniq = new Set();
    (Array.isArray(rawSymbols) ? rawSymbols : [])
      .map((s) => normalizeSymbol(s))
      .filter(Boolean)
      .forEach((s) => {
        if (uniq.size < maxSymbols) uniq.add(s);
      });
    return Array.from(uniq);
  }, [rawSymbols, maxSymbols]);

  const [prices, setPrices] = useState({});
  const [status, setStatus] = useState({ ok: true, msg: "" });
  const [isConnected, setIsConnected] = useState(false);

  const streamRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setStatus({ ok: false, msg: "Live prices disabled" });
      return;
    }
    
    if (!symbols.length) {
      setStatus({ ok: true, msg: "No symbols to track" });
      return;
    }

    // close any existing stream
    try {
      streamRef.current?.close?.();
    } catch {
      // ignore
    }
    streamRef.current = null;
    setIsConnected(false);

    setStatus({ ok: true, msg: "Connecting to price stream..." });

    let stream;
    try {
      stream = createPriceStream(symbols);
      streamRef.current = stream;
    } catch (e) {
      // If SSE fails, fallback to polling
      console.warn("SSE not available, falling back to polling:", e);
      setStatus({ ok: true, msg: "Using polling fallback" });
      
      // Polling fallback
      const pollInterval = setInterval(async () => {
        try {
          const results = await Promise.allSettled(
            symbols.map(s => getMarketPrice(s).catch(() => null))
          );
          
          const newPrices = {};
          results.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value) {
              const data = result.value;
              const symbol = data.symbol || symbols[index];
              newPrices[symbol] = {
                price: data.price,
                prevPrice: prices[symbol]?.price || null,
                ts: Date.now()
              };
            }
          });
          
          setPrices(prev => ({ ...prev, ...newPrices }));
          setStatus({ ok: true, msg: "Connected (polling)" });
          setIsConnected(true);
        } catch (error) {
          console.error("Polling error:", error);
        }
      }, 5000);
      
      return () => {
        clearInterval(pollInterval);
      };
    }

    stream.onMessage((payload) => {
      const now = Date.now();

      const applyOne = (sym, px, ts) => {
        const symbol = normalizeSymbol(sym);
        const price = Number(px);
        if (!symbol || !Number.isFinite(price)) return;

        setPrices((prev) => {
          const prevRow = prev[symbol];
          const prevPrice = prevRow?.price ?? null;

          return {
            ...prev,
            [symbol]: {
              price,
              prevPrice,
              ts: Number(ts) || now,
            },
          };
        });
      };

      if (!payload) return;

      if (payload.prices && typeof payload.prices === "object") {
        Object.entries(payload.prices).forEach(([sym, val]) => applyOne(sym, val, payload.ts));
        setStatus({ ok: true, msg: "Connected" });
        setIsConnected(true);
        return;
      }

      const sym = payload.symbol || payload.market || payload.s;
      const px = payload.price ?? payload.p ?? payload.last ?? payload.value;
      const ts = payload.ts || payload.time || payload.t;

      if (sym && px != null) {
        applyOne(sym, px, ts);
        setStatus({ ok: true, msg: "Connected" });
        setIsConnected(true);
      }
    });

    stream.onError((error) => {
      setStatus({ ok: false, msg: error?.message || "Live price stream error" });
      setIsConnected(false);
    });

    return () => {
      try {
        streamRef.current?.close?.();
      } catch {
        // ignore
      }
      streamRef.current = null;
      setIsConnected(false);
    };
  }, [enabled, symbols.join(",")]);

  return { 
    prices, 
    status,
    isConnected,
    getPrice: (symbol) => {
      const normalized = normalizeSymbol(symbol);
      return prices[normalized]?.price ?? null;
    },
    getChangePct: (symbol) => {
      const normalized = normalizeSymbol(symbol);
      const data = prices[normalized];
      if (!data || !data.prevPrice || data.prevPrice === 0) return 0;
      return ((data.price - data.prevPrice) / data.prevPrice) * 100;
    },
  };
}
